import React, {useId, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    IconButton,
    Skeleton,
    Text,
    chakra,
    useBreakpointValue,
    useStyleConfig,
} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import {Swiper, SwiperSlide} from 'swiper/react'
import {A11y, Keyboard, Mousewheel} from 'swiper/modules'
import {useProducts} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {ChevronLeftIcon, ChevronRightIcon} from '../../icons'
import CarouselTabs from './partials/carousel-tabs'
import CategoryCard from './partials/category-card'
import ProductCard from './partials/product-card'
import {CARD_VARIANTS, collectProductIds, getTabCards} from './utils'

const CarouselCardSlide = chakra(SwiperSlide)
CarouselCardSlide.displayName = 'CarouselCardSwiperSlide'

const MAX_SLIDES_PER_VIEW = 4.1

export const CarouselCards = ({title, subtitle, tabs = []}) => {
    const styles = useStyleConfig('CarouselCards')
    const intl = useIntl()
    const swiperStyles = useStyleConfig('Swiper')
    const instanceId = useId().replace(/:/g, '')
    const [activeTabIndex, setActiveTabIndex] = useState(0)
    const [isLocked, setIsLocked] = useState(true)
    const [shouldLoop, setShouldLoop] = useState(false)
    const swiperRef = useRef(null)
    const {locale} = useMultiSite()
    const categoriesData = useGetCategoriesData()

    const activeTab = tabs[activeTabIndex]
    const {variant, items} = useMemo(() => getTabCards(activeTab), [activeTab])

    const productIds = useMemo(() => collectProductIds(tabs), [tabs])
    const {data: productsData, isLoading} = useProducts(
        {
            parameters: {
                ids: productIds.join(','),
                expand: ['availability', 'images', 'prices', 'variations'],
            },
            headers: {'x-debug': 'carousel-cards-index'},
        },
        {enabled: productIds.length > 0},
    )

    const cards = useMemo(() => {
        if (variant === CARD_VARIANTS.CATEGORY) {
            return items.map((item) => {
                const {url, isAbsolute} = getGlobalLink({
                    link: item.link,
                    categoriesData,
                    productsData,
                    locale: locale?.alias || locale?.id?.toLowerCase(),
                })
                return {...item, url, isAbsolute}
            })
        }

        return items
            .map((item) => ({
                ...item,
                products: item.productIds
                    .map((id) => productsData?.data?.find((product) => product?.id === id))
                    .filter(Boolean),
            }))
            .filter((item) => item.products.length > 0)
    }, [variant, items, productsData, categoriesData, locale])

    const isHydrating = variant !== CARD_VARIANTS.CATEGORY && isLoading && productIds.length > 0

    const isMobile = useBreakpointValue({base: true, md: false}) ?? true
    const showArrows = !isMobile && !isLocked

    const updateSwiperState = (swiper) => {
        setIsLocked(swiper.isLocked)
        if (swiper.size > 0 && swiper.slidesSizesGrid.length >= cards.length) {
            const cardsWidth = swiper.slidesSizesGrid
                .slice(0, cards.length)
                .reduce((total, width) => total + width, 0)
            setShouldLoop(cards.length > 1 && cardsWidth > swiper.size)
        }
    }

    // Swiper needs at least ceil(slidesPerView) + 1 slides to loop at every breakpoint.
    const copies = shouldLoop ? Math.ceil((Math.ceil(MAX_SLIDES_PER_VIEW) + 1) / cards.length) : 1
    const slides = Array.from({length: copies}, () => cards).flat()

    // Below 768px every variant uses its fixed CSS width; 2.3 per view shrinks product tiles to ~130px
    const breakpoints = {
        768: {slidesPerView: 2.3},
        1024: {slidesPerView: 3.1},
        1440: {slidesPerView: MAX_SLIDES_PER_VIEW},
    }

    if (!tabs.length) return null

    const navigationButton = (direction) => (
        <IconButton
            className={`carousel-cards-${direction}-${instanceId}`}
            aria-label={intl.formatMessage(
                direction === 'prev'
                    ? {
                          id: 'carousel_cards.previous',
                          defaultMessage: 'Previous cards',
                      }
                    : {
                          id: 'carousel_cards.next',
                          defaultMessage: 'Next cards',
                      },
            )}
            variant="unstyled"
            {...styles?.navButton}
            {...(direction === 'prev' ? styles?.navButtonPrev : styles?.navButtonNext)}
            sx={{
                boxShadow: `${styles?.navButton?.boxShadow} !important`,
                '&:hover, &:active, &:focus, &:focus-visible, &:disabled': {
                    boxShadow: `${styles?.navButton?.boxShadow} !important`,
                },
            }}
            onClick={() => {
                if (direction === 'prev') swiperRef.current?.slidePrev()
                else swiperRef.current?.slideNext()
            }}
            icon={
                direction === 'prev' ? (
                    <ChevronLeftIcon {...styles?.navIcon} />
                ) : (
                    <ChevronRightIcon {...styles?.navIcon} />
                )
            }
        />
    )

    return (
        <Box
            {...styles?.container}
            data-testid="carousel-cards-container"
            data-card-variant={variant}
        >
            {(title || subtitle) && (
                <Flex {...styles?.header}>
                    {title && (
                        <Text as="h2" {...styles?.title}>
                            {title}
                        </Text>
                    )}
                    {subtitle && <Text {...styles?.subtitle}>{subtitle}</Text>}
                </Flex>
            )}

            <CarouselTabs
                tabs={tabs}
                activeIndex={activeTabIndex}
                onChange={setActiveTabIndex}
                idPrefix={`carousel-cards-${instanceId}`}
            />

            <Box
                id={`carousel-cards-${instanceId}-panel-${activeTabIndex}`}
                role={tabs.length > 1 ? 'tabpanel' : undefined}
                aria-labelledby={
                    tabs.length > 1
                        ? `carousel-cards-${instanceId}-tab-${activeTabIndex}`
                        : undefined
                }
                __css={{...swiperStyles, ...styles?.sliderContainer}}
                data-testid={`carousel-cards-slider-${variant}`}
                data-card-variant={variant}
            >
                {isHydrating ? (
                    <Flex {...styles?.skeletonRow}>
                        {[0, 1, 2].map((index) => (
                            <Skeleton key={index} {...styles?.skeletonCard} />
                        ))}
                    </Flex>
                ) : (
                    <Swiper
                        key={`${activeTabIndex}-${cards.length}-${shouldLoop}`}
                        modules={[Keyboard, A11y, Mousewheel]}
                        slidesPerView="auto"
                        breakpoints={breakpoints}
                        spaceBetween={0}
                        loop={shouldLoop}
                        roundLengths={true}
                        grabCursor={true}
                        keyboard={{enabled: true, onlyInViewport: true}}
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper
                            updateSwiperState(swiper)
                        }}
                        onBreakpoint={updateSwiperState}
                        onResize={updateSwiperState}
                        onSlidesUpdated={updateSwiperState}
                        onSlidesLengthChange={updateSwiperState}
                        onLock={updateSwiperState}
                        onUnlock={updateSwiperState}
                        watchOverflow={true}
                        mousewheel={{forceToAxis: true, sensitivity: 1, releaseOnEdges: true}}
                    >
                        {slides.map((card, index) => (
                            <CarouselCardSlide
                                key={`${card.key}-${index}`}
                                {...styles?.slideByVariant?.[variant]}
                            >
                                {variant === CARD_VARIANTS.CATEGORY ? (
                                    <CategoryCard
                                        title={card.title}
                                        subtitle={card.subtitle}
                                        image={card.image}
                                        iconId={card.iconId}
                                        icon={card.icon}
                                        url={card.url}
                                        isAbsolute={card.isAbsolute}
                                    />
                                ) : (
                                    <ProductCard
                                        variant={variant}
                                        products={card.products}
                                        characteristics={card.characteristics}
                                    />
                                )}
                            </CarouselCardSlide>
                        ))}
                    </Swiper>
                )}
            </Box>
            {showArrows && (
                <Flex {...styles?.navigationRow}>
                    {navigationButton('prev')}
                    {navigationButton('next')}
                </Flex>
            )}
        </Box>
    )
}

CarouselCards.propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    tabs: PropTypes.array,
}

export default CarouselCards
